import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Mail, ArrowUpRight, Loader2 } from "lucide-react";
import useSeo from "@/lib/useSeo";
import { EASY_MASSAGE_URL } from "@/lib/assets";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", industry: "General enquiry", message: "" };

export default function Contact() {
  useSeo({
    title: "Contact Easy Booking Solutions | Get Your Business Booking Online",
    description:
      "Get in touch with Easy Booking Solutions. Tell us about your business and we'll help you launch a beautiful, fully-bookable website for your industry.",
    path: "/contact",
  });

  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! Your message has been sent. We'll be in touch shortly.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-gray-200 px-4 py-3 text-[#0A0A0A] placeholder:text-gray-400 focus:outline-none focus:border-[#3EA65B] focus:ring-2 focus:ring-[#3EA65B]/20 transition-all";

  return (
    <div data-testid="contact-page">
      <section className="px-6 md:px-12 pt-20 pb-24">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-14">
          <div>
            <span className="text-xs md:text-sm font-bold tracking-[0.2em] uppercase text-[#3EA65B]">
              Contact us
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-[#0A0A0A] mt-4">
              Let's get your business booking online.
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed mt-6">
              Tell us a little about your business and we'll show you how Easy Booking can help.
              Prefer to explore first? Take a look at Easy Massage.
            </p>

            <a
              href={EASY_MASSAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-easymassage-link"
              className="inline-flex items-center gap-2 mt-8 font-semibold text-[#0A0A0A] hover:text-[#3EA65B] transition-colors"
            >
              Visit Easy Massage <ArrowUpRight className="h-4 w-4" />
            </a>

            <div className="mt-10 flex items-center gap-3 text-gray-600">
              <span className="h-11 w-11 rounded-full bg-[#F4FBF6] flex items-center justify-center">
                <Mail className="h-5 w-5 text-[#3EA65B]" />
              </span>
              <div>
                <p className="text-sm text-gray-500">Enquiries go straight to our team</p>
                <p className="font-semibold text-[#0A0A0A]">We'll reply by email</p>
              </div>
            </div>
          </div>

          <motion.form
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm"
          >
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Name</label>
                <input
                  data-testid="contact-name-input"
                  type="text"
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Your name"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Email</label>
                <input
                  data-testid="contact-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@example.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Industry</label>
                <Select value={form.industry} onValueChange={(v) => update("industry", v)}>
                  <SelectTrigger data-testid="contact-industry-select" className="rounded-xl border-gray-200 py-6">
                    <SelectValue placeholder="Select an industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General enquiry">General enquiry</SelectItem>
                    <SelectItem value="Massage / Day Spa">Massage / Day Spa</SelectItem>
                    <SelectItem value="Hair Salon">Hair Salon</SelectItem>
                    <SelectItem value="Nails">Nails</SelectItem>
                    <SelectItem value="Barber">Barber</SelectItem>
                    <SelectItem value="Auto Detailing">Auto Detailing</SelectItem>
                    <SelectItem value="Pet Grooming">Pet Grooming</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">Message</label>
                <textarea
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Tell us about your business..."
                  rows={5}
                  className={inputCls}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-button"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#0A0A0A] text-white px-8 py-4 font-semibold hover:bg-gray-800 transition-all disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Send message <Send className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </motion.form>
        </div>
      </section>
    </div>
  );
}
